import {
  VALIDATION_MESSAGES,
  HEAT_NUMBER_LENGTH,
} from "../components/modals/edit-location-modal/constants/locationContentEdition";
import { getStoredCraneId, getStoredOperatorId } from "./localStorage";

// Interface for manual entry data
export interface ManualEntryData {
  heatNumber: string;
  length: string;
  billetsCount: string;
}

// Interface for validation errors
export interface ValidationErrors {
  heatNumber?: string;
  length?: string;
  billetsCount?: string;
}

/**
 * Validates manual entry data for billet creation
 * @param entryData - The entry data to validate
 * @returns Object containing validation errors
 */
export const validateEntryData = (
  entryData: ManualEntryData,
): ValidationErrors => {
  const errors: ValidationErrors = {};

  // Heat Number validation - matching old system (exactly 6 characters)
  if (!entryData.heatNumber.trim()) {
    errors.heatNumber = VALIDATION_MESSAGES.INSERT_HEAT_NUMBER_FIRST;
  } else if (entryData.heatNumber.trim().length !== HEAT_NUMBER_LENGTH) {
    errors.heatNumber = VALIDATION_MESSAGES.INVALID_HEAT_NUMBER_LENGTH;
  }

  // Length validation - matching old system (positive number > 0)
  if (!entryData.length.trim()) {
    errors.length = VALIDATION_MESSAGES.INSERT_LENGTH_FIRST;
  } else {
    const lengthNum = parseFloat(entryData.length);
    if (isNaN(lengthNum) || lengthNum <= 0) {
      errors.length = VALIDATION_MESSAGES.INVALID_LENGTH_ZERO;
    }
  }

  // Billets Count validation - matching old system (positive integer > 0)
  if (!entryData.billetsCount.trim()) {
    errors.billetsCount = VALIDATION_MESSAGES.INSERT_BILLETS_COUNT_FIRST;
  } else {
    const countNum = parseInt(entryData.billetsCount);
    if (isNaN(countNum) || countNum <= 0) {
      errors.billetsCount = VALIDATION_MESSAGES.INVALID_BILLETS_COUNT_ZERO;
    }
  }

  return errors;
};

/**
 * Validates the complete form data for adding billets
 * @param entryData - Manual entry data
 * @param selectedSections - Selected sections array
 * @param selectedGrades - Selected grades array
 * @param selectedQualities - Selected qualities array
 * @param selectedDestinations - Selected destinations array (can include null for Unknown)
 * @param positionId - Selected position ID
 * @returns Error message string or null if valid
 */
export const validateAddBilletForm = (
  entryData: ManualEntryData,
  selectedSections: string[],
  selectedGrades: string[],
  selectedQualities: string[],
  selectedDestinations: (string | null)[],
  positionId?: number | null,
): string | null => {
  // 1. Section validation - must have selection (Unknown option not available)
  if (selectedSections.length === 0) {
    return VALIDATION_MESSAGES.SELECT_SECTION_FIRST;
  }
  // 2. Grade validation - must have selection and not be empty
  else if (selectedGrades.length === 0) {
    return VALIDATION_MESSAGES.SELECT_GRADE_FIRST;
  } else if (!selectedGrades[0] || selectedGrades[0].trim() === "") {
    return "Please select a valid grade";
  }
  // 3. Position validation
  else if (positionId === undefined || positionId === null) {
    return VALIDATION_MESSAGES.SELECT_POSITION_FIRST;
  }
  // 4. Quality validation - must have selection and not be empty
  else if (selectedQualities.length === 0) {
    return VALIDATION_MESSAGES.SELECT_QUALITY_FIRST;
  } else if (!selectedQualities[0] || selectedQualities[0].trim() === "") {
    return "Please select a valid quality status";
  }
  // 5. Destination validation - can be null (Unknown) but must have selection
  else if (selectedDestinations.length === 0) {
    return VALIDATION_MESSAGES.SELECT_DESTINATION_FIRST;
  }
  // 6. Heat Number presence validation
  else if (!entryData.heatNumber.trim()) {
    return VALIDATION_MESSAGES.INSERT_HEAT_NUMBER_FIRST;
  }
  // 7. Heat Number length validation (exactly 6 characters)
  else if (entryData.heatNumber.trim().length !== HEAT_NUMBER_LENGTH) {
    return VALIDATION_MESSAGES.INVALID_HEAT_NUMBER_LENGTH;
  }
  // 8. Length presence validation
  else if (!entryData.length.trim()) {
    return VALIDATION_MESSAGES.INSERT_LENGTH_FIRST;
  }
  // 9. Length numeric validation - matching old system exact messages
  else if (isNaN(parseFloat(entryData.length))) {
    return VALIDATION_MESSAGES.INVALID_LENGTH_FORMAT;
  } else if (parseFloat(entryData.length) <= 0) {
    return VALIDATION_MESSAGES.INVALID_LENGTH_ZERO;
  }
  // 10. Billets Count presence validation
  else if (!entryData.billetsCount.trim()) {
    return VALIDATION_MESSAGES.INSERT_BILLETS_COUNT_FIRST;
  }
  // 11. Billets Count numeric validation - matching old system exact messages
  else if (isNaN(parseInt(entryData.billetsCount))) {
    return VALIDATION_MESSAGES.INVALID_BILLETS_COUNT_FORMAT;
  } else if (parseInt(entryData.billetsCount) <= 0) {
    return VALIDATION_MESSAGES.INVALID_BILLETS_COUNT_ZERO;
  }

  return null; // No validation errors
};

/**
 * Checks if entry data is valid for form submission
 * @param entryData - The entry data to check
 * @returns True if valid, false otherwise
 */
export const hasValidEntryData = (entryData: ManualEntryData): boolean => {
  return !!(
    entryData.heatNumber.trim() &&
    entryData.length &&
    parseFloat(entryData.length) > 0 &&
    entryData.billetsCount &&
    parseInt(entryData.billetsCount) > 0
  );
};

// Authentication validation functions

export const isValidCraneId = (craneId: number | null): boolean => {
  return craneId !== null && craneId !== 0 && craneId !== -1;
};

export const isValidOperatorId = (operatorId: number | null): boolean => {
  return operatorId !== null && operatorId !== -1;
};
